import { Params, useParams, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from "recoil";
import { ContractListByBusinessLineSelector } from "store/data/Contract";
import { RouteToBusinessLine } from "./base";
import {
  MixSpotConfigListSelector,
  MixSpotConfigListState,
} from "store/data/Spot";
import { useRef, useState } from "react";
import { spotAllSymbol } from "api/ct";
import { isSuccess } from "util/Common";
import useIsomorphicLayoutEffect from "@app/utils/use-isomorphic-layout-effect";
import { CurrentBLineContractListLodable } from "store/data/Strategy";
import { useLodableValue } from "hooks";
// DOGE/USDT -> doge-usdt
export function convertSymbolDisplayName(symbolDisplayName: string): string {
  return symbolDisplayName.toLowerCase().replace(/\//g, "-");
}
// doge-usdt 转成DOGE/USDT
export function convertSymbol(symbol: string): string {
  return symbol.toUpperCase().replace(/-/g, "/");
}
// doge-usdt -> DOGEUSDT_SPBL
export function convertSymbolToFormat(symbol: string): string {
  return symbol.toUpperCase().replace(/-/g, "") + "_SPBL";
}
export enum FuturePath {
  usdt = "UMCBL", // u本位合约
  susdt = "SUMCBL", // u本位合约模拟
  usd = "DMCBL", // 币本位永续合约, 币本位交割合约
  susd = "SDMCBL", // 币本位合约模拟
  usdc = "CMCBL", // usdc本位合约
  susdc = "SCMCBL", // usdc本位合约模拟
}

export enum SpotPath {}

export enum StrategyPath {
  spot = "SPBL",
  futures = "UMCBL",
}
// 现货部分 跳转专用
const spotSuffix = "_SPBL";

type spotParams = {
  locales?: string;
  businessLine?: string;
  symbolId?: string;
  symbolIdUnFix?: string;
};

type futuresParams = {
  locales?: string;
  businessLine?: string;
  symbolId?: string; //这里以后肯定就废弃了
  symbolIdUnFix?: string;
  symbolDisplayName?: string;
};

//输出 去掉业务线后缀的币对
export const spotUrlForward = (symbolId: string) => {
  if (symbolId && symbolId.includes(spotSuffix)) {
    return symbolId.split(spotSuffix)[0];
  }
  return symbolId;
};

//合约-去掉symbol后缀 暂时保留兜底后期干掉
const removeContractSuffix = (symbolId: string) => {
  if (symbolId && symbolId.includes("_")) {
    const symbolIdStrArr = symbolId.split("_");
    const newSymbolId = symbolIdStrArr[0];
    const timeStr = symbolIdStrArr[2];
    // 永续合约去掉后缀，交割合约保持不变
    return timeStr === undefined ? newSymbolId : symbolId;
  }
  return symbolId;
};

//解析 现货 useParams
export const useSpotUrlParams = (): spotParams => {
  let paramsObj = useParams();
  let spotConfigList = useRecoilValue(MixSpotConfigListState);
  const currentSpotList = spotConfigList.length ? spotConfigList : null;
  let curConfig = null;
  if (currentSpotList) {
    // 这里的paramsObj.symbolId 含义变了 symbolCodeDisplayName 涉及到路由配置改动 暂时没改
    curConfig = currentSpotList.find(
      (item) => item.symbolCodeDisplayName === paramsObj.symbolId?.toUpperCase()
    );
    if (!curConfig) {
      return {
        ...paramsObj,
        locales: paramsObj.locales || "en",
        symbolId: "DEFAULT",
      };
    }
  }
  return {
    ...paramsObj,
    locales: paramsObj.locales || "en",
    symbolId: curConfig?.symbolId || "",
    symbolIdUnFix: curConfig?.symbolCode || undefined, //这里可能有问题哈
  };
};

/**
 * 路由不再是symbolId
 * @returns
 */
export const useFuturesUrlParams = (): futuresParams => {
  let paramsObj = useParams();
  let realBusinessLine = RouteToBusinessLine[paramsObj.businessLine];
  let curBusinessLineConfig = useRecoilValueLoadable(
    ContractListByBusinessLineSelector(realBusinessLine)
  );
  //是交割就直接返回了
  if (paramsObj.symbolId?.includes("_")) {
    return {
      ...paramsObj,
      symbolId: paramsObj.symbolId,
    };
  }

  const currentBusinessLineContractList =
    curBusinessLineConfig.state === "hasValue"
      ? curBusinessLineConfig.contents
      : [];
  let curConfig = null;
  if (currentBusinessLineContractList.length) {
    // 这里的paramsObj.symbolId 含义变了 symbolCodeDisplayName 涉及到路由配置改动 暂时没改
    curConfig = currentBusinessLineContractList.find(
      (item) => item.symbolCodeDisplayName === paramsObj.symbolId?.toUpperCase()
    );
    //做兜底
    if (!curConfig) {
      return {
        ...paramsObj,
        symbolId: "DEFAULT",
      };
    }
  }
  return {
    ...paramsObj,
    symbolId: curConfig?.symbolId || "",
  };
};

/**
 * 策略专用 这里的合约接口只有u本位的哦
 * @returns
 */
export const useStrategyUrlParams = (): futuresParams => {
  let paramsObj = useParams();
  const currentBusinessLineContractList = useLodableValue(
    CurrentBLineContractListLodable
  );
  let curConfig = null;
  if (currentBusinessLineContractList.length) {
    // 拿url中的symbolId换成真实的symbolId
    let currentSymbol = paramsObj.symbolId?.toUpperCase().replace(/-/g, "");
    curConfig = currentBusinessLineContractList.find(
      (item) => item.symbolCode === currentSymbol
    );
    // if(curConfig){
    //     realSymbolId = curConfig?.symbolId || realSymbolId
    // }
  }
  return {
    ...paramsObj,
    symbolId: curConfig?.symbolId || "",
  };
};

export const generateSymbolId = (symbolId: string): string => {
  if (symbolId.includes("_")) {
    return symbolId.split("_")[0];
  } else {
    return symbolId;
  }
};

export const useGetBusinessByPath = () => {
  const { pathname, search } = useLocation();

  const spotReg = new RegExp("spot/[A-Z\\d$]+");
  const futureReg = new RegExp("futures/(usdt|susdt|usd|susd|usdc|susdc)");
  const oldFutureReg = new RegExp("mix/(usdt|susdt|usd|susd|usdc|susdc)");
  const strategyReg = new RegExp("trading-bot/(futures|spot)/[A-Z\\d$]");
  const oldSpotStrategyReg = new RegExp("spot-strategy/[A-Z\\d$]");
  const oldFutureStrategyReg = new RegExp("future-strategy/[A-Z\\d$]");
  const path = pathname + search;
  if (
    strategyReg.test(path) ||
    oldSpotStrategyReg.test(path) ||
    oldFutureStrategyReg.test(path)
  ) {
    return "strategy";
  }
  if (spotReg.test(path)) {
    return "spot";
  }
  if (futureReg.test(path) || oldFutureReg.test(path)) {
    return "future";
  }
  return "strategy";
};

export const strategyBusinessLine = (pathName: string) => {
  if (pathName.match("trading-bot/spot")) {
    return "spot-strategy";
  }
  if (pathName.match("trading-bot/futures")) {
    return "future-strategy";
  }
  if (pathName.match("spot-strategy")) {
    return "spot-strategy";
  }
  if (pathName.match("future-strategy")) {
    return "future-strategy";
  }
};
export const useUrlParams = (): Readonly<Params<string>> => {
  // 根据 location.pathname 判断业务
  let type = useGetBusinessByPath();
  const { pathname } = useLocation();
  const params = useParams();
  let futuresUrlParams = useFuturesUrlParams();
  let spotUrlParams = useSpotUrlParams();
  //策略访问合约的时候是没有业务线的 这里的合约接口只有u本位的哦
  let symbolId = "";
  if (type === "spot") {
    symbolId = spotUrlParams.symbolId;
  }
  if (type === "future") {
    symbolId = futuresUrlParams.symbolId;
  }
  // 追加后缀的方式不再适用  直接区分现货合约策略 走现货合约的取symbolId的方式
  let strBusinessLine = "";
  if (type === "strategy") {
    strBusinessLine = strategyBusinessLine(pathname);
    let strategyParams = useStrategyUrlParams();
    symbolId = strategyParams.symbolId;
  }
  return {
    ...params,
    businessLine: strBusinessLine ? strBusinessLine : params.businessLine,
    symbolId,
  };
};

/**
 * 合约-根据symbolId找出对应的 symbolCodeDisplayName 如果找不到就直接去掉后缀走老逻辑吧
 * allContractList 所有币对 所有币对
 * @param1 : 所有合约的币对列表
 * @param2 : 对应业务线
 * @param3 : inputSymbolId 指定的symbolId
 */
export const grabContractSymbolCodeDisplayName = (
  allContractList,
  businessLine,
  inputSymbolId
) => {
  //交割直接返回
  const symbolIdStrArr = inputSymbolId.split("_");
  const timeStr = symbolIdStrArr[2];
  // 永续合约去掉后缀，交割合约保持不变
  if (timeStr !== undefined) {
    return inputSymbolId;
  }

  const find = allContractList.find(
    (item) => item.businessLine === businessLine
  );
  let curBusinessList = [];
  if (find) {
    curBusinessList = find.symbolDTOList.filter((i) => {
      // 线上要拼比掉betaShow为true的交易对
      if (process.env.REACT_APP_ENV === "online") {
        return !i.betaShow;
      } else {
        return true;
      }
    });
  } else {
    curBusinessList = [];
  }
  const curConfig = curBusinessList.find(
    (item) => item.symbolId === inputSymbolId
  );
  // return (
  //   curConfig?.symbolCodeDisplayName || removeContractSuffix(inputSymbolId)
  // );
  return convertSymbolDisplayName(curConfig?.symbolDisplayName);
};

/**
 * 现货-根据symbolId找出对应的 symbolCodeDisplayName 如果找不到就直接去掉后缀走老逻辑吧
 * @param1 : 现货的币对列表
 * @param2 : inputSymbolId 指定的symbolId
 */
export const grabSpotSymbolCodeDisplayName = (
  allSpotSymbolList,
  inputSymbolId
) => {
  const curConfig =
    allSpotSymbolList?.find((item) => item.symbolId === inputSymbolId) || {};
  // console.log(curConfig, allSpotSymbolList, inputSymbolId, "curConfig");
  // return (
  //   curConfig?.symbolCodeDisplayName || removeContractSuffix(inputSymbolId)
  // );
  return convertSymbolDisplayName(curConfig?.symbolDisplayName);
};

// export const strategyUrlReverse = params => {
//     if (params.symbolId) {
//         if (params.symbolId.includes('_')) {
//             return params.symbolId
//         } else {
//             return `${params.symbolId}_${addSuffix()}`
//         }
//     }
//     return params.symbolId
// }

// export const useStrategyUrlParams = (): spotParams => {
//     const params = useParams()

//     return {
//         businessLine: strategyBusinessLine(),
//         symbolId: strategyUrlReverse(params)
//     }
// }

// const addSuffix = () => {
//     const { pathname } = useLocation()

//     if (pathname.match('trading-bot/spot')) {
//         return StrategyPath.spot
//     }
//     if (pathname.match('trading-bot/futures')) {
//         return StrategyPath.futures
//     }
//     if (pathname.match('spot-strategy')) {
//         return StrategyPath.spot
//     }
//     if (pathname.match('future-strategy')) {
//         return StrategyPath.futures
//     }
// }

// //补充 业务线后缀的币对
// export const spotUrlReverse = paramsObj => {
//     if (!paramsObj.symbolId) {
//         return undefined
//     }
//     if (paramsObj.symbolId.includes(spotSuffix)) {
//         return paramsObj.symbolId
//     }
//     return paramsObj.symbolId + spotSuffix
// }

// // 合约-根据合约类型设置后缀
// const addContractSuffix = (symbolId: string) => {
//     const { businessLine } = useParams()
//     switch (businessLine) {
//         case 'usdt':
//             return FuturePath.usdt
//         case 'susdt':
//             return FuturePath.susdt
//         case 'usd':
//             // 币本位永续合约和交割合约都是/usd路径，需要再判断一次,永续添加后缀，交割合约保持不动
//             if (symbolId && symbolId.includes('_')) {
//                 let delivery = symbolId.split('_').length === 3
//                 return delivery ? '' : FuturePath.usd
//             } else {
//                 return FuturePath.usd
//             }
//         case 'susd':
//             return FuturePath.susd
//         case 'usdc':
//             return FuturePath.usdc
//         case 'susdc':
//             return FuturePath.susdc
//         default:
//             return ''
//     }
// }

// //合约-添加symbol后缀
// export const futureUrlReverse = paramsObj => {
//     if (!paramsObj.symbolId) {
//         return undefined
//     }
//     if (paramsObj.symbolId.includes('_')) {
//         return paramsObj.symbolId
//     }
//     return `${paramsObj.symbolId}_${addContractSuffix(paramsObj.symbolId)}`
// }
